/**
 * Eden Treaty client for the Portal API.
 *
 * Uses credentials: 'include' to send httpOnly session cookies with every request.
 * No Bearer tokens or refresh interceptors needed — Better Auth manages sessions.
 *
 * A global onResponse hook intercepts 401 responses: clears the session and
 * redirects to /login so the user doesn't see silent failures on expired tokens.
 *
 * fetchWithResilience wraps every request with:
 *   - Offline detection: shows a toast and rejects immediately if navigator.onLine is false
 *   - 30s AbortController timeout per attempt
 *   - Up to 2 retries for transient failures (502/503/504 and network errors)
 */
import { createApiClient } from '@solidafy/api-client';
import { config } from './config';
import { toast } from '@/lib/toast';

let redirecting = false;

const TIMEOUT_MS = 30_000;
const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchWithResilience(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!navigator.onLine) {
    toast.error('You appear to be offline — check your connection');
    throw new TypeError('Failed to fetch: offline');
  }

  const callerSignal = init?.signal ?? null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    // Wire the caller's signal into the timeout controller so both sources of
    // cancellation abort the in-flight request.
    const onCallerAbort = () => controller.abort(callerSignal!.reason);
    callerSignal?.addEventListener('abort', onCallerAbort);

    try {
      // If the caller already aborted before we started, throw without calling fetch.
      if (callerSignal?.aborted) {
        throw callerSignal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
      }

      const response = await fetch(input, { ...init, signal: controller.signal });

      if (RETRYABLE_STATUSES.has(response.status) && attempt < MAX_RETRIES) {
        await response.body?.cancel();
        await sleep(500 * (attempt + 1));
        continue;
      }

      return response;
    } catch (err) {
      // Caller explicitly cancelled — do not retry.
      if (callerSignal?.aborted) {
        throw err;
      }
      // Only retry on expected transient failures: network errors (TypeError) and
      // our own timeout AbortError. Rethrow everything else immediately so
      // programmer errors and unexpected exceptions are not silently swallowed.
      const isNetworkError = err instanceof TypeError;
      const isTimeoutAbort = err instanceof DOMException && err.name === 'AbortError';
      if ((isNetworkError || isTimeoutAbort) && attempt < MAX_RETRIES) {
        await sleep(500 * (attempt + 1));
        continue;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    }
  }

  // Unreachable — loop always returns or throws within the retry bound
  throw new Error('unreachable');
}

export const api = createApiClient(config.apiUrl, {
  fetch: { credentials: 'include' },
  fetcher: fetchWithResilience as typeof fetch,
  // Disable Eden Treaty's auto Date coercion — query results contain ISO timestamps
  // that should stay as strings. Date objects cause {0 keys} in table rendering.
  parseDate: false,
  onResponse: (response) => {
    if (response.status === 401 && !redirecting) {
      // Skip if already on the login page
      if (window.location.pathname.startsWith('/login')) return;

      redirecting = true;
      // Lazy import to avoid circular dependency (auth store imports api)
      void import('@/stores/auth')
        .then(({ useAuthStore }) => {
          useAuthStore.getState().clearAuth();
        })
        .catch(() => {});
      toast.error('Session expired — please log in again');
      setTimeout(() => {
        window.location.replace('/login');
      }, 100);
    }
  },
});
